import { useEffect, useState } from 'react';

// One place that asks the browser about the viewport, so components and CSS agree on where
// the breakpoints are. SSR/jsdom-safe: no matchMedia means "no match", which resolves every
// caller to its desktop branch — the same answer the server would give.

/** Breakpoints shared with the CSS. Keep these in step with engine.css / shell.css. */
export const PHONE_QUERY = '(max-width: 640px)';
export const TABLET_QUERY = '(max-width: 900px)';
/** A finger rather than a mouse: no hover, imprecise pointer. */
export const COARSE_QUERY = '(pointer: coarse)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange(); // the query may have changed between render and effect
    // addListener is the Safari < 14 spelling; both are cleaned up below.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

/** Phone-sized viewport: one column, bottom tab bar, cards instead of a table. */
export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/** Tablet or narrower: the sidebar is off-canvas from here down. */
export function useIsNarrow(): boolean {
  return useMediaQuery(TABLET_QUERY);
}

/** Touch input — used to widen hit areas and drop hover-only affordances. */
export function useIsCoarsePointer(): boolean {
  return useMediaQuery(COARSE_QUERY);
}
